import React from 'react';
import { ArrowRight, ArrowLeft, Check, Monitor, Smartphone, Zap, Search, Layout, Paintbrush, RefreshCw, Target, Users, Building2, Briefcase, User, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CookieBanner from '../components/layout/CookieBanner';

const trustPoints = [
  'Conversion-focused design',
  'Mobile-first & lightning fast',
  'SEO-ready structure',
  'Built for real business growth'
];

const whoWeHelp = [
  { icon: Building2, title: 'Small & Medium Businesses' },
  { icon: Zap, title: 'Startups & SaaS Companies' },
  { icon: Briefcase, title: 'Service-Based Businesses' },
  { icon: User, title: 'Coaches & Consultants' },
  { icon: MapPin, title: 'Local & National Brands' }
];

const approach = [
  'User experience (UX) strategy',
  'Conversion-optimized layouts',
  'Clean, modern design',
  'Speed & performance optimization',
  'SEO-friendly structure'
];

const services = [
  {
    icon: Paintbrush,
    title: 'Custom Website Design',
    description: 'Fully custom designs tailored to your brand, audience, and business goals.'
  },
  {
    icon: RefreshCw,
    title: 'Website Redesign',
    description: 'Turn an outdated or underperforming site into a modern, high-converting machine.'
  },
  {
    icon: Target,
    title: 'Landing Pages',
    description: 'Conversion-focused pages built for ads, lead generation, and sales funnels.'
  },
  {
    icon: Smartphone,
    title: 'Mobile-First Design',
    description: 'Your site will look and perform perfectly on phones, tablets, and desktops.'
  },
  {
    icon: Zap,
    title: 'Speed & Performance',
    description: 'Fast websites rank higher, convert better, and keep visitors engaged.'
  },
  {
    icon: Search,
    title: 'SEO-Ready Foundations',
    description: 'Clean code, optimized structure, and best practices baked in from day one.'
  }
];

const whyChooseUs = [
  'Business-first mindset (not just design trends)',
  'Conversion-focused layouts',
  'Clear messaging & calls-to-action',
  'Fast turnaround times',
  'Transparent communication'
];

const process = [
  {
    step: '01',
    title: 'Discovery & Strategy',
    description: 'We learn your business, goals, audience, and competitors.'
  },
  {
    step: '02',
    title: 'Wireframes & UX Planning',
    description: 'We map out the structure and user flow before design begins.'
  },
  {
    step: '03',
    title: 'Design & Development',
    description: 'We bring the vision to life with clean, modern design and high-quality development.'
  },
  {
    step: '04',
    title: 'Review & Revisions',
    description: 'We refine the site until it\'s exactly right.'
  },
  {
    step: '05',
    title: 'Launch & Optimization',
    description: 'Your site goes live, optimized for speed, SEO, and conversions.'
  }
];

const results = [
  'Higher engagement & longer time on site',
  'More leads, bookings, or sales',
  'Stronger brand credibility',
  'A website that actually supports your growth'
];

const WebDevelopment = () => {
  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[85vh] flex flex-col justify-center overflow-hidden bg-[#f8f7f4] pt-32 pb-16">
          {/* Gradient blob background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute top-0 right-0 w-[80%] h-[120%] opacity-50"
              style={{
                background: `
                  radial-gradient(ellipse 70% 50% at 75% 20%, rgba(147, 51, 234, 0.3) 0%, transparent 50%),
                  radial-gradient(ellipse 60% 50% at 85% 50%, rgba(59, 130, 246, 0.25) 0%, transparent 50%),
                  radial-gradient(ellipse 50% 60% at 65% 70%, rgba(236, 72, 153, 0.2) 0%, transparent 50%)
                `,
                filter: 'blur(60px)',
                transform: 'rotate(-10deg)'
              }}
            />
          </div>

          <div className="relative z-10 px-6 md:px-12 lg:px-24">
            {/* Back link */}
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-gray-600 hover:text-black transition-colors mb-12"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>

            <div className="max-w-4xl">
              <span className="text-sm uppercase tracking-widest text-gray-500 mb-4 block">Web Development</span>
              <h1 className="text-[clamp(2.5rem,5vw,4.5rem)] font-light leading-[1.1] text-black mb-6">
                Websites That Don't Just Look Good —{' '}
                <span className="font-medium">They Convert</span>
              </h1>
              <p className="text-xl text-gray-700 max-w-2xl mb-4">
                High-performance web design built to attract, engage, and turn visitors into customers.
              </p>
              <p className="text-lg text-gray-600 max-w-2xl mb-8">
                We design modern, fast, and conversion-focused websites that help businesses grow. 
                Whether you need a brand-new site or a redesign, we build digital experiences that work.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-colors"
                >
                  Get a Free Strategy Call <ArrowRight size={18} />
                </a>
                <Link
                  to="/#work"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-black text-black rounded-full font-medium hover:bg-black hover:text-white transition-colors"
                >
                  View Our Work
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Strip */}
        <section className="py-8 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="flex flex-wrap justify-center gap-8 md:gap-16">
              {trustPoints.map((point, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Check size={18} className="text-green-400" />
                  <span className="text-sm md:text-base">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who We Help */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Who We Help
              </h2>
              <p className="text-lg text-gray-600">
                We design websites for businesses that want results, not just visuals.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
              {whoWeHelp.map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                  <item.icon className="w-10 h-10 text-gray-700 mx-auto mb-4" />
                  <h3 className="text-sm font-medium text-black">{item.title}</h3>
                </div>
              ))}
            </div>

            <p className="text-center text-lg text-gray-700 font-medium">
              If your website isn't bringing you leads or sales, we fix that.
            </p>
          </div>
        </section>

        {/* What We Do */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                  Strategic Web Design That Drives Results
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Your website is your most important digital asset. We don't just design pages — 
                  we build systems that guide visitors to take action.
                </p>
                <div className="mb-8">
                  <p className="text-gray-700 font-medium mb-4">Our web design approach combines:</p>
                  <ul className="space-y-3">
                    {approach.map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check size={18} className="text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-lg text-black font-medium">
                  Every decision is made with one goal in mind: growth.
                </p>
              </div>
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/143bnhb6_marketing-agency-western-sydney.jpg" 
                    alt="Web Design Portfolio - Multiple devices showing responsive websites"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Services */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="mb-16">
              <h2 className="text-[clamp(3rem,8vw,8rem)] font-light text-gray-300 leading-none tracking-tight">
                <span className="block">OUR</span>
                <span className="block ml-[10%]">SERVICES</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all group">
                  <service.icon className="w-10 h-10 text-gray-400 group-hover:text-black mb-4 transition-colors" />
                  <h3 className="text-xl font-medium text-black mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-medium mb-6">
                  We Design With Purpose
                </h2>
                <p className="text-gray-400 text-lg mb-8">
                  Most websites fail because they're built without strategy. We design with clarity, 
                  intention, and data-backed best practices.
                </p>
                <div className="mb-8">
                  <p className="text-white font-medium mb-4">What sets us apart:</p>
                  <ul className="space-y-3">
                    {whyChooseUs.map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check size={18} className="text-green-400 flex-shrink-0" />
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-xl text-white font-medium">
                  Your website should be your best salesperson — we make sure it is.
                </p>
              </div>
              <div className="relative">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-800 rounded-2xl p-6">
                    <Monitor className="w-8 h-8 text-purple-400 mb-3" />
                    <p className="text-white font-medium">Desktop</p>
                    <p className="text-gray-500 text-sm">Optimized</p>
                  </div>
                  <div className="bg-gray-800 rounded-2xl p-6">
                    <Smartphone className="w-8 h-8 text-blue-400 mb-3" />
                    <p className="text-white font-medium">Mobile</p>
                    <p className="text-gray-500 text-sm">First</p>
                  </div>
                  <div className="bg-gray-800 rounded-2xl p-6">
                    <Zap className="w-8 h-8 text-yellow-400 mb-3" />
                    <p className="text-white font-medium">Fast</p>
                    <p className="text-gray-500 text-sm">Loading</p>
                  </div>
                  <div className="bg-gray-800 rounded-2xl p-6">
                    <Search className="w-8 h-8 text-green-400 mb-3" />
                    <p className="text-white font-medium">SEO</p>
                    <p className="text-gray-500 text-sm">Ready</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Process */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Our Process
              </h2>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              {process.map((item, index) => (
                <div key={index} className="relative">
                  <span className="text-6xl font-light text-gray-200">{item.step}</span>
                  <h3 className="text-lg font-medium text-black mt-2 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                  {index < process.length - 1 && (
                    <div className="hidden md:block absolute top-8 right-0 w-full h-px bg-gray-200 -mr-4"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-12">
                Results You Can Expect
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {results.map((result, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-sm flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Check size={20} className="text-green-600" />
                    </div>
                    <p className="text-gray-800 font-medium text-left">{result}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-light mb-6">
                Ready to Build a Website{' '}
                <span className="font-medium">That Works?</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Let's create a website that attracts the right audience and turns visitors into customers.
              </p>
              <a
                href="mailto:hello@gomobyle.com"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black rounded-full font-medium hover:bg-gray-200 transition-colors mb-4"
              >
                Book a Free Website Strategy Call <ArrowRight size={18} />
              </a>
              <p className="text-gray-500 text-sm">
                No pressure. No obligation. Just clarity.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
};

export default WebDevelopment;
